import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useStore from "../../services/store";
import api from "../../services/api";
import Loader from "../../components/loader";
import SelectUser from "../../components/selectUser";
import MultiSelect from "../../components/MultiSelect";
import { getDateKey, getUTCDateKey } from "../../utils";

// ---- Date helpers ----
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getSunday = (date) => {
  const monday = getMonday(date);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday;
};

// ---- TACE color (0% = deep red so holes stand out in a dense grid) ----
const getTaceColor = (tace) => {
  if (tace <= 0) return "bg-red-600 text-white";
  if (tace >= 80) return "bg-green-500 text-white";
  if (tace >= 60) return "bg-green-300";
  if (tace >= 40) return "bg-yellow-300";
  return "bg-orange-300";
};

// ---- Hatch patterns (unsigned share of a cell) ----
const HATCH_DARK = "repeating-linear-gradient(135deg, rgba(17,24,39,0.35) 0px, rgba(17,24,39,0.35) 3px, transparent 3px, transparent 7px)";
const HATCH_GREY = "repeating-linear-gradient(135deg, rgba(107,114,128,0.55) 0px, rgba(107,114,128,0.55) 3px, transparent 3px, transparent 7px)";

// ---- Position colors ----
const POSITION_COLORS = {
  FULLSTACK: { bg: "bg-blue-100", text: "text-blue-700" },
  CTO: { bg: "bg-purple-100", text: "text-purple-700" },
  DATA: { bg: "bg-cyan-100", text: "text-cyan-700" },
  DEVOPS: { bg: "bg-orange-100", text: "text-orange-700" },
  PRODUCT: { bg: "bg-green-100", text: "text-green-700" },
  SALES: { bg: "bg-pink-100", text: "text-pink-700" },
  OTHER: { bg: "bg-gray-100", text: "text-gray-600" },
};
const getPositionStyle = (position) => POSITION_COLORS[position] || POSITION_COLORS.OTHER;

// ---- Constants ----
const TACE_TARGET = 80; // % — signed occupancy target
const WEEK_HOURS = 40; // 5 working days × 8h
const TIME_OFF_PROJECTS = ["unavailable", "leave paid", "rtt", "leave unpaid", "arret maladie", "holiday", "holliday", "congé sans solde", "timeoff"];
const CONTRACT_OPTIONS = ["CDI", "CDD", "VIE", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"];
const DEFAULT_CONTRACT_FILTERS = [
  { value: "CDI", label: "CDI" },
  { value: "CDD", label: "CDD" },
  { value: "VIE", label: "VIE" },
  { value: "FREELANCE", label: "FREELANCE" },
];
const SORT_OPTIONS = [
  { value: "default", label: "À replacer" },
  { value: "name-asc", label: "Name A→Z" },
  { value: "name-desc", label: "Name Z→A" },
  { value: "tace-asc", label: "Avg TACE ↑" },
  { value: "tace-desc", label: "Avg TACE ↓" },
];
const PREDICTION_MODES = [
  { value: "hide", label: "Masquer", title: "Signé seulement — le non signé passe en grisé" },
  { value: "weighted", label: "Pondérer", title: "Charge × % de confiance du budget" },
  { value: "full", label: "100 %", title: "Tout compté au nominal (vue historique)" },
];
const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FUTURE_WEEKS = 16;
const PAST_WEEKS = 4;

const fmtDays = (d) => {
  const r = Math.round(d * 10) / 10;
  return Number.isInteger(r) ? `${r}` : r.toFixed(1);
};

// ---- Sub-components ----

const ConsultantCell = ({ consultant, isHovered, flagged }) => {
  const posStyle = getPositionStyle(consultant.position);

  return (
    <td
      className={`px-3 py-2 sticky left-0 z-10 w-[220px] transition-colors ${flagged ? "border-l-4 border-l-red-500" : ""} ${
        isHovered ? "bg-yellow-100" : "bg-white"
      }`}>
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">
          {consultant.avatar ? (
            <img src={consultant.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-500">
              {(consultant.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-gray-900 truncate">{consultant.name}</span>
            {consultant.non_billable && (
              <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Non-billable</span>
            )}
            {consultant.contract === "INTERNSHIP" && (
              <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 font-medium">Intern</span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            {consultant.position && (
              <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${posStyle.bg} ${posStyle.text}`}>{consultant.position}</span>
            )}
            <span className="text-[10px] text-gray-400 truncate">
              {[consultant.contract, consultant.office].filter(Boolean).join(" · ")}
            </span>
          </div>
          {consultant.exitingSoon && (
            <div className="text-[10px] text-red-500 truncate mt-0.5" title={`Exit: ${new Date(consultant.exit_date).toLocaleDateString()}`}>
              ⚠️ Exits {new Date(consultant.exit_date).toLocaleDateString()}
            </div>
          )}
          {consultant.latestBudgetEnd && (
            <div
              className={`text-[10px] truncate ${consultant.budgetEndingSoon ? "text-amber-600 font-medium" : "text-gray-400"}`}
              title={`Budget: ${consultant.latestBudgetName || "N/A"}\nEnds: ${consultant.latestBudgetEnd.toLocaleDateString()}`}>
              📋 {consultant.latestBudgetName ? `${consultant.latestBudgetName} → ` : ""}
              {consultant.latestBudgetEnd.toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </td>
  );
};

const TaceCell = ({ weekData: wd, weekMeta, consultant, mode, isHovered, onMouseEnter }) => {
  const isOut = consultant.exit_date && weekMeta && new Date(consultant.exit_date) < weekMeta.monday;
  const isIncoming = consultant.starting_date && weekMeta && new Date(consultant.starting_date) > weekMeta.sunday;

  if (isOut || isIncoming) {
    const cellContent = isOut ? "OUT" : "INCOMING";
    const cellClass = isOut
      ? "bg-gray-300 text-gray-500"
      : "bg-blue-50 text-blue-400 text-[7px] font-normal leading-none px-0 py-1 whitespace-nowrap tracking-[-0.06em]";
    const cellTitle = isOut
      ? `${consultant.name} - Left before ${weekMeta?.label}`
      : `${consultant.name} - Not started yet (starts ${new Date(consultant.starting_date).toLocaleDateString()})`;
    return (
      <td
        className={`px-3 py-3 text-center text-sm font-medium transition-all ${cellClass} ${isHovered ? "ring-2 ring-yellow-400 ring-inset" : ""}`}
        onMouseEnter={onMouseEnter}
        title={cellTitle}>
        {cellContent}
      </td>
    );
  }

  if (wd.capacity <= 0) {
    return (
      <td
        className={`px-1 py-3 text-center text-sm font-medium transition-all ${getTaceColor(100)} ${isHovered ? "ring-2 ring-yellow-400 ring-inset" : ""}`}
        onMouseEnter={onMouseEnter}
        title={`${consultant.name} - ${weekMeta?.label}\nAbsence: ${fmtDays(wd.offHours / 8)}j (semaine entière)`}>
        100%
      </td>
    );
  }

  // Hours counted by the active prediction mode
  let displayedHours = wd.weightedHours;
  if (mode === "hide") displayedHours = wd.signedHours;
  if (mode === "full") displayedHours = wd.totalHours;

  const pct = Math.min((displayedHours / wd.capacity) * 100, 100);
  // Hours booked on unsigned budgets, at their nominal (unweighted) value
  const nominalUnsigned = wd.totalHours - wd.signedHours;
  // Unsigned contribution in the active mode. In "hide" it is not counted but still shown greyed.
  const contribution = mode === "weighted" ? wd.weightedHours - wd.signedHours : nominalUnsigned;
  // Hours the hatched band is measured against. In "hide" the cell shows signed only, so we fall back to the brut load.
  const chargeBase = mode === "hide" ? wd.totalHours : displayedHours;
  // Width of the hatched band, as a share of the charge base
  const bandPct = chargeBase > 0 ? Math.min((contribution / chargeBase) * 100, 100) : 0;
  // Nothing at all is signed this week → hatch the whole cell instead of a right-hand band
  const fullyUnsigned = wd.signedHours <= 0 && nominalUnsigned > 0;
  // Badge number: unsigned contribution as a share of the week's capacity
  const badgePts = Math.round((contribution / wd.capacity) * 100);
  const showRisk = nominalUnsigned > 0 && bandPct > 0;

  const signedPct = (wd.signedHours / wd.capacity) * 100;
  const weightedPct = (wd.weightedHours / wd.capacity) * 100;
  const brutPct = (wd.totalHours / wd.capacity) * 100;
  const lines = [
    `${consultant.name} - ${weekMeta?.label} (${weekMeta?.monthLabel})`,
    `Signé ${Math.round(signedPct)}% · Pondéré ${Math.round(weightedPct)}% · Brut ${Math.round(brutPct)}%`,
    `Capacité: ${fmtDays(wd.capacity / 8)}j${wd.offHours > 0 ? ` (absences ${fmtDays(wd.offHours / 8)}j)` : ""}`,
  ];
  wd.assignments.forEach((a) => lines.push(`• ${a.name}: ${fmtDays(a.hours / 8)}j ${a.confidence >= 100 ? "(signé)" : `@${a.confidence}%`}`));

  return (
    <td
      className={`relative px-1 py-3 text-center text-sm font-medium transition-all ${getTaceColor(pct)} ${isHovered ? "ring-2 ring-yellow-400 ring-inset" : ""}`}
      onMouseEnter={onMouseEnter}
      title={lines.join("\n")}>
      {showRisk && !fullyUnsigned && (
        <div
          className="absolute inset-y-0 right-0 pointer-events-none"
          style={{ width: `${bandPct}%`, backgroundImage: mode === "hide" ? HATCH_GREY : HATCH_DARK, borderLeft: "1.5px dashed rgba(17,24,39,0.6)" }}
        />
      )}
      {showRisk && fullyUnsigned && (
        <div
          className="absolute inset-0 pointer-events-none border-2 border-dashed border-gray-800/60"
          style={{ backgroundImage: mode === "hide" ? HATCH_GREY : HATCH_DARK }}
        />
      )}
      {showRisk && badgePts > 0 && (
        <span
          className="absolute top-0.5 right-0.5 z-10 text-[10px] font-bold leading-none px-1 py-0.5 bg-white text-amber-700 rounded border border-amber-300 shadow"
          title={`${badgePts}% de la capacité non signée, confiance min ${wd.minConfidence ?? 0}%`}>
          {badgePts}%@{wd.minConfidence ?? 0}
        </span>
      )}
      <span className="relative">{Math.round(pct)}%</span>
    </td>
  );
};

const ConsultantDetail = ({ consultant, weeks, mode, onClose, onOpenActivity }) => {
  // Aggregate projects over current + future weeks
  const projects = {};
  weeks.forEach((week, i) => {
    if (week.isPast) return;
    (consultant.weeklyData[i]?.assignments || []).forEach((a) => {
      if (!projects[a.name]) projects[a.name] = { days: 0, minConfidence: a.confidence };
      projects[a.name].days += a.hours / 8;
      projects[a.name].minConfidence = Math.min(projects[a.name].minConfidence, a.confidence);
    });
  });
  const projectList = Object.entries(projects).sort((a, b) => b[1].days - a[1].days);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[440px] max-w-full h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center gap-3 z-10">
          {consultant.avatar ? (
            <img src={consultant.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-500">
              {(consultant.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-gray-900 truncate">{consultant.name}</div>
            <div className="text-[11px] text-gray-400 truncate">{[consultant.position, consultant.contract, consultant.office].filter(Boolean).join(" · ")}</div>
          </div>
          <button onClick={onOpenActivity} className="text-xs px-2 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 whitespace-nowrap">
            Activité complète →
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none px-1">✕</button>
        </div>

        <div className="p-4 space-y-5">
          {/* Projects */}
          <div>
            <div className="text-[10px] uppercase text-gray-400 font-medium mb-2">Projets (semaines à venir)</div>
            {projectList.length === 0 && <div className="text-sm text-gray-400">Aucun projet affecté.</div>}
            <div className="space-y-1.5">
              {projectList.map(([name, p]) => (
                <div key={name} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate text-gray-800">{name}</span>
                  <span className="flex-shrink-0 flex items-center gap-1.5">
                    <span className="font-bold text-gray-900">{fmtDays(p.days)}j</span>
                    {p.minConfidence >= 100 ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">signé</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">@{p.minConfidence}%</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Week by week */}
          <div>
            <div className="text-[10px] uppercase text-gray-400 font-medium mb-2">Semaine par semaine</div>
            <div className="space-y-1">
              {weeks.map((week, i) => {
                if (week.isPast) return null;
                const wd = consultant.weeklyData[i];
                if (!wd) return null;
                // Hours counted by the active prediction mode
                let displayedHours = wd.weightedHours;
                if (mode === "hide") displayedHours = wd.signedHours;
                if (mode === "full") displayedHours = wd.totalHours;
                // Week fully off: no capacity left to fill, shown as a full week
                const pct = wd.capacity <= 0 ? 100 : Math.min((displayedHours / wd.capacity) * 100, 100);
                return (
                  <div key={i} className={`px-2 py-1.5 rounded text-xs ${week.isCurrent ? "bg-blue-50" : "bg-gray-50"}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-14 font-medium text-gray-700">{week.label} <span className="text-gray-400 font-normal">{week.dateLabel}</span></span>
                      <div className="flex-1 h-2 bg-gray-200 rounded overflow-hidden">
                        <div className={`h-full ${getTaceColor(pct)}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-9 text-right font-bold text-gray-800">{Math.round(pct)}%</span>
                    </div>
                    {wd.assignments.length > 0 && (
                      <div className="mt-1 pl-16 space-y-0.5">
                        {wd.assignments.map((a, j) => (
                          <div key={j} className="flex items-center justify-between gap-2 text-[11px] text-gray-500">
                            <span className="truncate">{a.name}</span>
                            <span className="flex-shrink-0">
                              {fmtDays(a.hours / 8)}j {a.confidence >= 100 ? <span className="text-green-600">signé</span> : <span className="text-amber-600">@{a.confidence}%</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Main component ----

const Tace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const organisation = useStore((state) => state.organisation);
  const businessUnit = useSelector((state) => state.Auth.businessUnit);

  const [loading, setLoading] = useState(true);
  const [consultants, setConsultants] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [hoveredCell, setHoveredCell] = useState({ row: null, col: null });
  const [selectedConsultant, setSelectedConsultant] = useState(null);

  // Prediction mode: hide | weighted | full
  const [predictionMode, setPredictionMode] = useState("weighted");

  // Manager filter
  const [manager, setManager] = useState(null);
  const [manageesOfManagees, setManageesOfManagees] = useState(false);

  // Scroll
  const [weekOffset, setWeekOffset] = useState(0);

  // Filters
  const [contractFilters, setContractFilters] = useState(DEFAULT_CONTRACT_FILTERS);
  const [businessUnitFilters, setBusinessUnitFilters] = useState([]); // [{ value, label }]
  const [hideNonBillable, setHideNonBillable] = useState(true);
  const [searchFilter, setSearchFilter] = useState("");
  const [sortBy, setSortBy] = useState("default");

  // ---- Generate weeks (past + current + future) ----
  useEffect(() => {
    const weeksArray = [];
    const today = new Date();
    const currentMonday = getMonday(today);

    for (let i = PAST_WEEKS; i >= 1; i--) {
      const monday = new Date(currentMonday);
      monday.setDate(monday.getDate() - i * 7);
      const sunday = getSunday(monday);
      weeksArray.push({
        monday, sunday,
        weekNum: getWeekNumber(monday),
        label: `W${getWeekNumber(monday)}`,
        monthLabel: monthNames[monday.getMonth()],
        dateLabel: `${monday.getDate()}/${monday.getMonth() + 1}`,
        isCurrent: false, isFuture: false, isPast: true,
        month: monday.getMonth(), year: monday.getFullYear(),
      });
    }

    weeksArray.push({
      monday: currentMonday, sunday: getSunday(currentMonday),
      weekNum: getWeekNumber(currentMonday),
      label: `W${getWeekNumber(currentMonday)}`,
      monthLabel: monthNames[currentMonday.getMonth()],
      dateLabel: `${currentMonday.getDate()}/${currentMonday.getMonth() + 1}`,
      isCurrent: true, isFuture: false, isPast: false,
      month: currentMonday.getMonth(), year: currentMonday.getFullYear(),
    });

    const totalFuture = FUTURE_WEEKS + weekOffset;
    for (let i = 1; i <= Math.max(0, totalFuture); i++) {
      const monday = new Date(currentMonday);
      monday.setDate(monday.getDate() + i * 7);
      const sunday = getSunday(monday);
      weeksArray.push({
        monday, sunday,
        weekNum: getWeekNumber(monday),
        label: `W${getWeekNumber(monday)}`,
        monthLabel: monthNames[monday.getMonth()],
        dateLabel: `${monday.getDate()}/${monday.getMonth() + 1}`,
        isCurrent: false, isFuture: true, isPast: false,
        month: monday.getMonth(), year: monday.getFullYear(),
      });
    }
    setWeeks(weeksArray);
  }, [weekOffset]);

  // ---- Fetch data ----
  useEffect(() => {
    if (weeks.length === 0) return;
    fetchData();
  }, [manager?._id, manageesOfManagees, weeks, businessUnit]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const userQuery = {
        role: ["admin", "user", "super_admin"],
        contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
        status: "active",
      };
      if (manager) {
        userQuery.manager_id = manager._id;
        userQuery.managees_of_managees = manageesOfManagees;
      }
      if (businessUnit._id !== "ALL") {
        userQuery.business_unit_id = businessUnit._id;
      } else if (organisation?._id && organisation._id !== "ALL") {
        userQuery.organisation_id = organisation._id;
      }

      const { data: usersData, ok } = await api.post(`/user/search`, userQuery);
      if (!ok || !usersData?.users) return;

      const today = new Date();
      const allConsultants = usersData.users;

      // Fetch forecast activities
      const firstWeek = weeks[0];
      const lastWeek = weeks[weeks.length - 1];
      if (!firstWeek || !lastWeek) return;
      const firstMonthStart = new Date(Date.UTC(firstWeek.monday.getFullYear(), firstWeek.monday.getMonth(), 1));
      const lastMonthEnd = new Date(lastWeek.sunday.getFullYear(), lastWeek.sunday.getMonth() + 1, 0);

      const { data: activitiesData } = await api.post(`/activity/search`, {
        userIds: allConsultants.map((u) => u._id),
        dateFrom: firstMonthStart.getTime(),
        dateTo: lastMonthEnd.getTime(),
      });

      // Budgets referenced by activities → confidence per assignment (read-only, set on the budget)
      const budgetIds = [...new Set((activitiesData || []).map((a) => a.budgetId).filter(Boolean))];
      const budgetById = {};
      if (budgetIds.length > 0) {
        try {
          const { data: budgetsByIdData } = await api.post("/budget/search", { ids: budgetIds });
          (budgetsByIdData || []).forEach((b) => (budgetById[b._id] = b));
        } catch (e) {
          console.error("Failed to fetch budgets by ids", e);
        }
      }

      // Budgets for end-date info
      const projectIdsFromActivities = (activitiesData || []).map((a) => a.projectId).filter(Boolean);
      const uniqueProjectIds = [...new Set(projectIdsFromActivities)];
      let allBudgets = [];
      if (uniqueProjectIds.length > 0) {
        try {
          const { data: budgetsData } = await api.post("/budget/search", { projectIds: uniqueProjectIds, status: "active" });
          allBudgets = (budgetsData || []).filter((b) => b.endAt);
        } catch (e) {
          console.error("Failed to fetch budgets", e);
        }
      }

      // Calculate weekly TACE (forecast only)
      const consultantsWithTace = allConsultants.map((consultant) => {
        const consultantActivities = (activitiesData || []).filter((activity) => activity.userId === consultant._id);

        const weeklyData = weeks.map((week) => {
          let offHours = 0;
          // Forecast is stored per day; a week straddling two months reads two activities of the same budget, merged into one assignment.
          const from = getDateKey(week.monday);
          const to = getDateKey(week.sunday);
          const assignmentsByBudget = {};
          consultantActivities.forEach((activity) => {
            const hours = (activity.predicted_detail || []).reduce((sum, day) => {
              const key = getUTCDateKey(day.date);
              return key >= from && key <= to ? sum + (day.value || 0) : sum;
            }, 0);
            if (!(hours > 0)) return;

            const projectType = (activity.projectType || "").toLowerCase();
            const projectName = (activity.projectName || "").toLowerCase().trim();
            // Time off reduces capacity, it is not an assignment
            if (projectType === "timeoff" || TIME_OFF_PROJECTS.includes(projectName)) {
              offHours += hours;
              return;
            }

            // Confidence inherited from the project's budget. Signed = 100%. Internal/admin work is firm.
            let confidence = 100;
            const budget = budgetById[activity.budgetId];
            if (projectType !== "admin" && budget && !budget.signed_at && (!budget.type || budget.type === "project")) {
              confidence = budget.lost ? 0 : Math.max(0, Math.min(budget.confidence || 0, 100));
            }
            const budgetKey = activity.budgetId || activity.projectId;
            if (assignmentsByBudget[budgetKey]) assignmentsByBudget[budgetKey].hours += hours;
            else assignmentsByBudget[budgetKey] = { name: activity.projectName || activity.budgetName || "?", hours, confidence };
          });
          const assignments = Object.values(assignmentsByBudget);

          const capacity = Math.max(0, WEEK_HOURS - offHours);
          const signedHours = assignments.reduce((sum, a) => sum + (a.confidence >= 100 ? a.hours : 0), 0);
          const weightedHours = assignments.reduce((sum, a) => sum + (a.hours * a.confidence) / 100, 0);
          const totalHours = assignments.reduce((sum, a) => sum + a.hours, 0);
          const unsigned = assignments.filter((a) => a.confidence < 100);
          const minConfidence = unsigned.length > 0 ? Math.min(...unsigned.map((a) => a.confidence)) : null;

          return { capacity, offHours, signedHours, weightedHours, totalHours, minConfidence, assignments };
        });

        // Avg brut occupancy (used by the TACE sort options)
        const weeksWithCapacity = weeklyData.filter((wd) => wd.capacity > 0);
        const avgOccupation =
          weeksWithCapacity.length > 0
            ? weeksWithCapacity.reduce((sum, wd) => sum + Math.min((wd.totalHours / wd.capacity) * 100, 100), 0) / weeksWithCapacity.length
            : 0;

        // "À replacer" stats — signed only, current + future weeks
        let freeDays = 0;
        let emptyWeeks = 0;
        weeks.forEach((week, i) => {
          if (week.isPast) return;
          const isOut = consultant.exit_date && new Date(consultant.exit_date) < week.monday;
          const isIncoming = consultant.starting_date && new Date(consultant.starting_date) > week.sunday;
          if (isOut || isIncoming) return;
          const wd = weeklyData[i];
          if (wd.capacity <= 0) return;
          freeDays += Math.max(0, wd.capacity - wd.signedHours) / 8;
          if (wd.signedHours <= 0) emptyWeeks += 1;
        });

        // Budget info
        const consultantProjectIds = new Set(
          (activitiesData || []).filter((a) => a.userId === consultant._id).map((a) => a.projectId).filter(Boolean),
        );
        const consultantBudgets = allBudgets.filter(
          (b) => b.resources_needed?.some((r) => r.assignedUserId === consultant._id) || consultantProjectIds.has(b.projectId),
        );
        const latestBudgetEnd = consultantBudgets.reduce((latest, b) => {
          const end = new Date(b.endAt);
          return !latest || end > latest ? end : latest;
        }, null);
        const latestBudgetName = latestBudgetEnd
          ? consultantBudgets.find((b) => new Date(b.endAt).getTime() === latestBudgetEnd.getTime())?.name || null
          : null;

        const ninetyDaysFromNow = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);
        const exitingSoon = consultant.exit_date && new Date(consultant.exit_date) <= ninetyDaysFromNow && new Date(consultant.exit_date) >= today;
        const budgetEndingSoon = latestBudgetEnd && latestBudgetEnd <= ninetyDaysFromNow && latestBudgetEnd >= today;

        return {
          ...consultant, weeklyData, avgOccupation,
          freeDays, emptyWeeks,
          latestBudgetEnd, latestBudgetName, exitingSoon, budgetEndingSoon,
        };
      });

      setConsultants(consultantsWithTace);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openUserActivity = (consultantId) => {
    const params = new URLSearchParams(location.search);
    params.set("user_modal_id", consultantId);
    params.set("user_modal_tab", "activity");
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  // ---- Derived lists ----
  const uniqueBusinessUnits = useMemo(() => [...new Set(consultants.map((c) => c.business_unit_name).filter(Boolean))].sort(), [consultants]);

  // ---- Client-side filtering ----
  const filteredConsultants = useMemo(() => {
    let list = consultants.filter((c) => {
      if (hideNonBillable && c.non_billable) return false;
      if (contractFilters.length > 0 && !contractFilters.some((f) => f.value === c.contract)) return false;
      if (businessUnitFilters.length > 0 && !businessUnitFilters.some((f) => f.value === c.business_unit_name)) return false;
      if (searchFilter && !c.name?.toLowerCase().includes(searchFilter.toLowerCase())) return false;
      return true;
    });

    if (sortBy === "name-asc") list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortBy === "name-desc") list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    else if (sortBy === "tace-asc") list.sort((a, b) => a.avgOccupation - b.avgOccupation);
    else if (sortBy === "tace-desc") list.sort((a, b) => b.avgOccupation - a.avgOccupation);
    else list.sort((a, b) => b.emptyWeeks - a.emptyWeeks || b.freeDays - a.freeDays);

    return list;
  }, [consultants, contractFilters, businessUnitFilters, hideNonBillable, searchFilter, sortBy]);

  // ---- Counter ----
  const hiddenCount = consultants.length - filteredConsultants.length;

  const regularConsultants = filteredConsultants.filter((c) => !(c.contract === "FREELANCE" && c.payment_type === "DAILY"));
  const dailyFreelancers = filteredConsultants.filter((c) => c.contract === "FREELANCE" && c.payment_type === "DAILY");

  // ---- Per-column aggregates (hours, excl. daily freelancers and OUT/INCOMING) ----
  const colStats = useMemo(
    () =>
      weeks.map((week, i) => {
        let capacity = 0, signed = 0, weighted = 0, total = 0;
        filteredConsultants.forEach((c) => {
          if (c.contract === "FREELANCE" && c.payment_type === "DAILY") return;
          const isOut = c.exit_date && new Date(c.exit_date) < week.monday;
          const isIncoming = c.starting_date && new Date(c.starting_date) > week.sunday;
          if (isOut || isIncoming) return;
          const wd = c.weeklyData[i];
          if (!wd) return;
          capacity += wd.capacity;
          signed += wd.signedHours;
          weighted += wd.weightedHours;
          total += wd.totalHours;
        });
        return {
          capacity, signed, weighted, total,
          signedPct: capacity > 0 ? (signed / capacity) * 100 : 0,
          weightedPct: capacity > 0 ? (weighted / capacity) * 100 : 0,
          brutPct: capacity > 0 ? (total / capacity) * 100 : 0,
        };
      }),
    [weeks, filteredConsultants],
  );

  // ---- Rupture: current/future column with the lowest signed TACE ----
  const ruptureIndex = useMemo(() => {
    let idx = null;
    weeks.forEach((week, i) => {
      if (week.isPast || colStats[i].capacity <= 0) return;
      if (idx === null || colStats[i].signedPct < colStats[idx].signedPct) idx = i;
    });
    if (idx !== null && colStats[idx].signedPct >= TACE_TARGET) return null;
    return idx;
  }, [weeks, colStats]);

  // ---- Footer "Jours à vendre" (mode-dependent) ----
  const daysToSellByCol = useMemo(
    () =>
      weeks.map((week, i) => {
        if (week.isPast) return null;
        const cs = colStats[i];
        // Hours counted by the active prediction mode
        let displayedHours = cs.weighted;
        if (predictionMode === "hide") displayedHours = cs.signed;
        if (predictionMode === "full") displayedHours = cs.total;
        return Math.max(0, (TACE_TARGET / 100) * cs.capacity - displayedHours) / 8;
      }),
    [weeks, colStats, predictionMode],
  );

  // ---- Scroll ----
  const scrollMore = () => setWeekOffset((prev) => prev + 8);
  const resetScroll = () => setWeekOffset(0);

  const isDefaultContracts = contractFilters.length === DEFAULT_CONTRACT_FILTERS.length && DEFAULT_CONTRACT_FILTERS.every((d) => contractFilters.some((f) => f.value === d.value));
  const hasAnyFilter = searchFilter || !isDefaultContracts || businessUnitFilters.length > 0 || sortBy !== "default" || manager || !hideNonBillable;
  const resetAll = () => {
    setSearchFilter("");
    setContractFilters(DEFAULT_CONTRACT_FILTERS);
    setBusinessUnitFilters([]);
    setHideNonBillable(true);
    setSortBy("default");
    setManager(null);
    setManageesOfManagees(false);
  };

  if (loading) return <Loader />;

  const renderRow = (consultant, rowIndex, extraRowClass = "") => (
    <tr
      key={consultant._id}
      className={`border-b cursor-pointer transition-colors ${extraRowClass} ${hoveredCell.row === rowIndex ? "bg-yellow-50" : "hover:bg-gray-50"}`}
      onClick={() => setSelectedConsultant(consultant)}>
      <ConsultantCell consultant={consultant} isHovered={hoveredCell.row === rowIndex} flagged={consultant.emptyWeeks > 0} />
      {consultant.weeklyData.map((wd, colIndex) => (
        <TaceCell key={colIndex} weekData={wd} weekMeta={weeks[colIndex]} consultant={consultant} mode={predictionMode}
          isHovered={hoveredCell.row === rowIndex && hoveredCell.col === colIndex}
          onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })} />
      ))}
    </tr>
  );

  return (
    <div className="p-4">
      <div className="mb-4 space-y-3">
        {/* Row 1: Description + count */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <span className="font-medium text-gray-700">TACE Forecast</span> — signé / pondéré / brut · hachures = non signé · ⚠️ contract ending · 📋 budget ending
          </div>
          <span className="text-xs text-gray-400">
            {filteredConsultants.length} consultants
            {hiddenCount > 0 && (
              <span className="text-gray-300">
                {" "}({hiddenCount} hidden)
              </span>
            )}
          </span>
        </div>

        {/* Row 2: All filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <SelectUser
              value={manager}
              onChange={(u) => setManager(u)}
              userRoles={["admin", "user", "super_admin"]}
              placeholder="Manager..."
              className="w-44"
            />
            {manager && (
              <>
                <button onClick={() => setManager(null)} className="text-xs px-1.5 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-500" title="Clear manager">✕</button>
                <label className="flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer ml-1">
                  <input
                    type="checkbox"
                    checked={manageesOfManagees}
                    onChange={(e) => setManageesOfManagees(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
                  />
                  +sub
                </label>
              </>
            )}
          </div>

          <div className="w-px h-6 bg-gray-200" />

          <input
            type="text"
            placeholder="Search..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-36 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="w-44">
            <MultiSelect
              id="select-contract"
              options={CONTRACT_OPTIONS.map((c) => ({ value: c, label: c }))}
              values={contractFilters}
              onSelectedChange={setContractFilters}
              placeholder="Contract"
            />
          </div>
          <div className="w-44">
            <MultiSelect
              id="select-business-unit"
              options={uniqueBusinessUnits.map((o) => ({ value: o, label: o }))}
              values={businessUnitFilters}
              onSelectedChange={setBusinessUnitFilters}
              placeholder="Business Unit"
            />
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500">
            {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none ml-1">
            <input
              type="checkbox"
              checked={hideNonBillable}
              onChange={(e) => setHideNonBillable(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Hide non-billable
          </label>

          {hasAnyFilter && (
            <button onClick={resetAll} className="px-2.5 py-1.5 text-xs text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Reset all
            </button>
          )}
        </div>

        {/* Row 3: Week nav + prediction mode */}
        <div className="flex items-center gap-2">
          <button onClick={resetScroll}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${weekOffset === 0 ? "bg-blue-100 text-blue-700 border border-blue-300" : "bg-gray-100 border border-gray-300 hover:bg-gray-200 text-gray-600"}`}>
            -{PAST_WEEKS}w → Current → +{FUTURE_WEEKS}w
          </button>
          <button onClick={scrollMore} className="px-3 py-1 text-xs font-medium bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 text-gray-600">
            +8 weeks →
          </button>
          {weekOffset > 0 && (
            <span className="text-xs text-gray-400">Showing {weeks.length} weeks ({PAST_WEEKS} past + {FUTURE_WEEKS + weekOffset} future)</span>
          )}

          <div className="w-px h-6 bg-gray-200" />

          <span className="text-xs text-gray-500 font-medium">Prédictions</span>
          <div className="flex rounded-md border border-gray-300 overflow-hidden">
            {PREDICTION_MODES.map((m) => (
              <button
                key={m.value}
                onClick={() => setPredictionMode(m.value)}
                title={m.title}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  predictionMode === m.value ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 sticky left-0 bg-gray-50 z-10 w-[220px]">Consultant</th>
                {weeks.map((week, i) => {
                  const cs = colStats[i];
                  const isRupture = ruptureIndex === i;
                  return (
                    <th
                      key={`${week.weekNum}-${week.year}`}
                      title={`${week.label} (${week.monthLabel})\nSigné ${Math.round(cs.signedPct)}% · Pondéré ${Math.round(cs.weightedPct)}% · Brut ${Math.round(cs.brutPct)}%\nCapacité: ${fmtDays(cs.capacity / 8)}j`}
                      className={`px-2 py-2 text-center text-xs font-medium min-w-[70px] transition-colors ${
                        isRupture ? "bg-red-100 text-red-700 ring-1 ring-inset ring-red-400"
                        : hoveredCell.col === i ? "bg-yellow-100"
                        : week.isCurrent ? "bg-blue-100 text-blue-800"
                        : week.isPast ? "text-gray-400 bg-gray-50"
                        : "text-gray-700"
                      }`}>
                      <div className="font-bold">{week.label}</div>
                      <div className="text-[10px] opacity-60">{week.dateLabel}</div>
                      <div className="text-sm font-bold mt-0.5">
                        {Math.round(predictionMode === "hide" ? cs.signedPct : predictionMode === "full" ? cs.brutPct : cs.weightedPct)}%
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody onMouseLeave={() => setHoveredCell({ row: null, col: null })}>
              {/* Regular consultants */}
              {regularConsultants.map((consultant, rowIndex) => renderRow(consultant, rowIndex))}

              {/* Daily Freelancers */}
              {dailyFreelancers.length > 0 && (
                <>
                  <tr className="bg-orange-50 border-t-2 border-orange-300">
                    <td colSpan={weeks.length + 1} className="px-4 py-2 text-xs font-medium text-orange-700 sticky left-0 bg-orange-50 z-10">
                      Daily Freelancers
                    </td>
                  </tr>
                  {dailyFreelancers.map((consultant, idx) => renderRow(consultant, regularConsultants.length + idx, "bg-orange-50/30"))}
                </>
              )}

              {/* Footer: days to sell per week */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
                <td className="px-4 py-2 text-sm font-bold text-gray-700 sticky left-0 bg-gray-100 z-10 w-[220px]"
                  title={`max(0, ${TACE_TARGET}% × capacité − jours facturables du mode actif), en jours`}>
                  Jours à vendre
                </td>
                {weeks.map((_, i) => (
                  <td key={i} className={`px-2 py-2 text-center text-sm ${daysToSellByCol[i] > 0 ? "text-red-600 font-bold" : "text-gray-400"}`}>
                    {daysToSellByCol[i] === null ? "—" : `${fmtDays(daysToSellByCol[i])}j`}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {filteredConsultants.length === 0 && <div className="text-center py-8 text-gray-500">No consultants found.</div>}

      {selectedConsultant && (
        <ConsultantDetail
          consultant={selectedConsultant}
          weeks={weeks}
          mode={predictionMode}
          onClose={() => setSelectedConsultant(null)}
          onOpenActivity={() => {
            setSelectedConsultant(null);
            openUserActivity(selectedConsultant._id);
          }}
        />
      )}
    </div>
  );
};

export default Tace;
